import {useConstCallback} from '../hooks/useConstCallback';
import {useArrowFocusTrapProvider} from './ArrowFocusTrapContext';

export interface UseArrowFocusTrapItemParams {
	index: number;
	disabled?: boolean;
}

export const useArrowFocusTrapItem = ({
	index,
	disabled,
}: UseArrowFocusTrapItemParams) => {
	const arrowFocusTrap = useArrowFocusTrapProvider();

	const isActive =
		arrowFocusTrap.isFocusTrapActive &&
		!arrowFocusTrap.disabled &&
		arrowFocusTrap.activeItemIndex === index;

	const handleFocus = useConstCallback(
		(event: React.FocusEvent<HTMLElement>) => {
			arrowFocusTrap.setActiveItemIndex(index);
		},
	);

	const handleMouseDown = useConstCallback(
		(event: React.MouseEvent<HTMLElement>) =>
			arrowFocusTrap.handleItemInteractStart(index)(event),
	);

	const handleClick = useConstCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			arrowFocusTrap.handleItemClick(index)(event);
		},
	);

	const handleMenuOpen = useConstCallback(() => {
		arrowFocusTrap.setUserDisabled(true);
	});

	const handleMenuClose = useConstCallback(() => {
		arrowFocusTrap.setUserDisabled(false);
	});

	const handleActionButtonBlur = useConstCallback(() => {
		arrowFocusTrap.setClickKeysDisabled(false);
	});

	const handleActionButtonFocus = useConstCallback(() => {
		arrowFocusTrap.setClickKeysDisabled(true);
	});

	const handleActionMenuClick = useConstCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (!disabled) return;
			event.stopPropagation();
		},
	);

	const setActive = useConstCallback((active = true) => {
		arrowFocusTrap.setActiveItemIndex(active ? index : -1);
	});

	return {
		isActive,
		actionAttributes: {
			onClick: handleActionMenuClick,
			onBlur: handleActionButtonBlur,
			onFocus: handleActionButtonFocus,
		},
		attributes: {
			tabIndex: isActive ? 0 : -1,
			onFocus: handleFocus,
			onMouseDown: handleMouseDown,
			onClick: handleClick,
		},
		handleMenuClose,
		handleMenuOpen,
		setActive,
	};
};
